import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';

import { HttpClient, HttpResponse } from '@angular/common/http';

import { Observable, merge, of } from 'rxjs';
import { map, switchMap, tap, catchError } from 'rxjs/operators';

import { HotkeysService, Hotkey } from 'angular2-hotkeys';

import { environment } from '../../../environments/environment';

interface IApiResponse {
  confirmNumber: string;

  data: any;

  isLoading: boolean;
  isLoaded: boolean;
  isError: boolean;
  errorMessage: string;
};

@Component({
  selector: 'app-readonly',
  templateUrl: './readonly.component.html',
  styleUrls: ['./readonly.component.scss']
})
export class ReadonlyComponent implements OnInit {

  constructor(private route: ActivatedRoute, private http: HttpClient, private router: Router, private hotKeys: HotkeysService, private location: Location) {

    this.hotKeys.add(new Hotkey('ctrl+shift+b', (event: KeyboardEvent): boolean => {
      this.location.back();
      return false;
    }));

  }

  /**
   * atlas pricing of current SC reservation
   * @param confirmNumber 
   * @param atlasAirport allow Atlas HOR handling
   */
  priceAtlas(confirmNumber: string, atlasAirport: string) {
    const initial$: Observable<IApiResponse> = of(confirmNumber).pipe(
      map(cn => this.initialViewModel(confirmNumber))
    );

    const loader$: Observable<IApiResponse> = of(confirmNumber).pipe(
      switchMap(cn =>
        this.http.get(`${environment.scApiProtocol}://${environment.scApiHost}:${environment.scApiPort}/api/SantaCruz/PriceAtlas/json?confirmNumber=${cn}&airportCode=${atlasAirport}`)
          .pipe(
            map((r: any) => r.response),
            catchError(error => of(this.httpErrorViewModel(cn, error)))
          )
      ),
      map((r: any) => this.loadedViewModel(r))
    );

    this.viewState$ = merge(initial$, loader$);
  }

  viewState$: Observable<IApiResponse>;

  /**
   * inital view model
   * @param confirmNumber 
   */
  private initialViewModel(confirmNumber: string): IApiResponse {
    const ret: IApiResponse = {
      confirmNumber: confirmNumber,

      data: {},

      isLoading: true,
      isLoaded: false,
      isError: false,
      errorMessage: ''
    };
    return ret;
  }

  /**
   * error view model
   * @param confirmationNumber 
   * @param httpError 
   */
  private httpErrorViewModel(confirmationNumber: string, httpError: any): IApiResponse {
    const ret: IApiResponse = {
      isLoading: false,
      isLoaded: false,
      isError: true,
      errorMessage: httpError.error instanceof ErrorEvent ? `an error occurred: ${httpError.error.message}` : `backend error: ${httpError.status} - ${httpError.message}`,
      confirmNumber: confirmationNumber,
      data: {}
    };
    return ret;
  }

  /**
   * loaded view model
   * @param r response from server
   */
  private loadedViewModel(r: any): IApiResponse {
    var scTaxesFunc = (): number =>
      parseFloat(r.segment.pricing.taxesAndOther.taxes['@total']) + parseFloat(r.segment.pricing.expenses.custom['@total']);

    var atlasTaxesFunc = (): number =>
      r.atlas ? parseFloat(r.atlas.taxes) + parseFloat(r.atlas.wcTax) : 0.0;

    var atlasBaseFunc = (): number =>
      r.atlas ? parseFloat(r.atlas.baseRate) + parseFloat(r.atlas.lateNight) + parseFloat(r.atlas.extraStop) : 0.0;

    var atlasBillableMinutes = (): number =>
      r.atlas ? parseFloat(r.atlas.billableHours || 0) == 0 ? 0 : parseFloat(r.atlas.billableHours) * 60 : 0.0;

    var atlasTotalCost = (): number =>
      r.atlas && r.atlas.atlasTotalCost ? parseFloat(r.atlas.atlasTotalCost) : 0.0;

    var scTotalCost = (): number =>
      r.atlas && r.atlas.scTotalCost ? parseFloat(r.atlas.scTotalCost) : 0.0;

    var isAffiliateTrip = (): boolean =>
      r.atlas && r.atlas.department ? parseInt(r.atlas.department) >= 300 ? true : false : false;

    const ret: IApiResponse = r.isError === true ? r : {

      confirmNumber: r['@confirmNumber'],

      data: r.segment,
      dataAtlas: r.atlas,
      isAffiliateTrip: isAffiliateTrip(),

      dataExtra: {
        scTaxesTotal: scTaxesFunc(),
        atlasTaxesTotal: atlasTaxesFunc(),
        atlasBaseTotal: atlasBaseFunc(),
        atlasBillableMinutes: atlasBillableMinutes()
      },

      matches: r.atlas ? {
        total: Math.abs(parseFloat(r.atlas.total) - parseFloat(r.segment.pricing['@total'])) < .02,
        rateType: r.atlas.isRatedHourly === "true" && r.segment.pricing['@rateType'] === 'hourly' ? true
          : r.atlas.isRatedHourly === "false" && r.segment.pricing['@rateType'] === 'flat' ? true
            : false,
        baseRate: atlasBaseFunc() === parseFloat(r.segment.pricing.charges['@total']),
        tolls: parseFloat(r.atlas.tolls) === parseFloat(r.segment.pricing.expenses.tolls['@total']),
        parkingFee: parseFloat(r.atlas.parkingFee) === parseFloat(r.segment.pricing.expenses.parking['@total']),
        airportFee: parseFloat(r.atlas.airportFee) === parseFloat(r.segment.pricing.expenses.airportFee['@total']),
        grat: parseFloat(r.atlas.grat) === parseFloat(r.segment.pricing.expenses.gratuity['@total']),
        stf: Math.abs(parseFloat(r.atlas.stf) - parseFloat(r.segment.pricing.taxesAndOther.serviceCharges['@total'])) < .02,
        fsc: Math.abs(parseFloat(r.atlas.fsc) - parseFloat(r.segment.pricing.expenses.surcharges['@fuel'])) < .02,
        taxes: atlasTaxesFunc() === scTaxesFunc(),
        lateNight: parseFloat(r.atlas.lateNight) === parseFloat(r.segment.pricing.charges.surcharge['@offHour']),
        holiday: parseFloat(r.atlas.holiday) === parseFloat(r.segment.pricing.charges.surcharge['@holiday']),
        extraStop: parseFloat(r.atlas.extraStop) === parseFloat(r.segment.pricing.charges.stops['@total']),
        miscCode1: parseFloat(r.atlas.miscCode1) === parseFloat(r.segment.pricing.expenses.misc['@charge1']),
        miscCode2: parseFloat(r.atlas.miscCode2) === parseFloat(r.segment.pricing.expenses.misc['@charge2']),
        wcTax: parseFloat(r.atlas.wcTax) === parseFloat(r.segment.pricing.taxesAndOther.taxes['@wcTax']),
        billableMinutes: atlasBillableMinutes() === parseFloat(r.segment.pricing.charges.base['@billedNumberOfMinutes']),
        totalCost: atlasTotalCost() === scTotalCost()
      } : {},

      isFound: r['@status'] === 'notFound',

      isLoading: false,
      isLoaded: true,
      isError: false,
      errorMessage: ''
    };
    return ret;
  }

  ngOnInit() {

    const initial: Observable<IApiResponse> = this.route.params.pipe(
      map(v => v['confirmNumber']),
      map(cn => this.initialViewModel(cn)));

    const loader: Observable<IApiResponse> = this.route.params.pipe(
      map(v => v['confirmNumber']),
      switchMap(cn =>
        this.http.get(`${environment.scApiProtocol}://${environment.scApiHost}:${environment.scApiPort}/api/SantaCruz/Segment/json?confirmNumber=${cn}`)
          .pipe(
            map((r: any) => r.response),
            catchError(error => of(this.httpErrorViewModel(cn, error)))
          )
      ),
      map((r: any) => this.loadedViewModel(r))
    );

    this.viewState$ = merge(initial, loader);
  }

}
